import { useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import { postFavorite, postUnfavorite } from '../../api/locations';
import { Child, Parent } from '../../utils/api';
import queryKeys from '../queries/keys';

const ProfileContext = createContext<Parent | null>(null);
const ProfileActionsContext = createContext<ProfileActions>(
  {} as ProfileActions
);

type ProfileActions = {
  updateParent: (parentUpdate: Partial<Parent>) => void;
  updateChild: (id: number, child: Partial<Child>) => void;
  addChild: (child: Child) => void;
  toggleFavorite: (locationId: number) => void;
};

export default function ProfileProvider({
  parent: initialParent,
  children,
}: {
  parent: Parent;
  children: React.ReactNode;
}) {
  const queryClient = useQueryClient();

  const [parent, setParentValue] = useState(initialParent);

  const updateParent = useCallback(
    (
      update:
        | Partial<Omit<Parent, 'id'>>
        | ((parent: Parent) => Partial<Omit<Parent, 'id'>>)
    ) =>
      setParentValue((prevStateParent) => ({
        ...prevStateParent,
        ...(typeof update === 'function' ? update(prevStateParent) : update),
      })),
    []
  );

  const actions = useMemo<ProfileActions>(
    () => ({
      updateParent,
      updateChild(id: number, child: Partial<Child>) {
        updateParent((currentParent) => {
          return {
            children: currentParent.children.map((c) =>
              c.id === id
                ? {
                    ...c,
                    ...child,
                  }
                : c
            ),
          };
        });
      },
      addChild(child: Child) {
        updateParent((currentParent) => {
          return {
            children: [...currentParent.children, child],
          };
        });
      },
      async toggleFavorite(locationId) {
        const { favorites } = parent;

        const isFavorited = favorites.includes(locationId);

        const action = isFavorited ? postUnfavorite : postFavorite;

        const updatedFavorites = isFavorited
          ? favorites.filter((id) => id !== locationId)
          : [...favorites, locationId];

        updateParent({
          favorites: updatedFavorites,
        });

        await action(locationId);

        queryClient.invalidateQueries({
          queryKey: queryKeys.homeFavorites,
        });
      },
    }),
    [parent]
  );
  return (
    <ProfileContext.Provider value={parent}>
      <ProfileActionsContext.Provider value={actions}>
        {children}
      </ProfileActionsContext.Provider>
    </ProfileContext.Provider>
  );
}

export const useProfile = () => {
  const me = useContext(ProfileContext);
  if (!me) {
    throw new Error('useProfile used when not logged in!');
  }
  return me;
};

export const useProfileActions = () => {
  const update = useContext(ProfileActionsContext);
  if (!update) {
    throw new Error('useProfileActions outside of ProfileProvider');
  }
  return update;
};

export const useProfileChildren = () => {
  const me = useProfile();
  return me.children;
};
